import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, XCircle, Circle, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { apiClient } from '@/services/api';
import { timeAgo } from '@/utils/cn';

interface Execution {
  id: number;
  routine_id: number;
  agent_id: string;
  started_at: string;
  finished_at?: string | null;
  status: 'ok' | 'error' | 'partial' | 'empty';
  rows_processed?: number | null;
  duration_ms?: number | null;
  error_message?: string | null;
}

const STATUS_STYLES: Record<Execution['status'], { icon: React.ReactNode; tone: string }> = {
  ok:      { icon: <CheckCircle2 className="w-4 h-4 text-emerald-600" />, tone: 'border-emerald-200 bg-emerald-50' },
  error:   { icon: <XCircle      className="w-4 h-4 text-red-600" />,     tone: 'border-red-200 bg-red-50' },
  partial: { icon: <AlertCircle  className="w-4 h-4 text-amber-600" />,   tone: 'border-amber-200 bg-amber-50' },
  empty:   { icon: <Circle       className="w-4 h-4 text-zinc-400" />,    tone: 'border-zinc-200 bg-zinc-50' },
};

export function ExecutionsLogPage() {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [routineFilter, setRoutineFilter] = useState<number | undefined>();
  const [limit, setLimit] = useState(100);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const { data: routines = [] } = useQuery({
    queryKey: ['agent-etl', 'routines'],
    queryFn: () => apiClient.agentEtl.listRoutines().then(r => r.data.routines as Array<{ id: number; name: string }>),
  });

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['agent-etl', 'executions', { statusFilter, routineFilter, limit }],
    queryFn: () => apiClient.agentEtl.listExecutions({
      status: statusFilter === 'all' ? undefined : statusFilter,
      routineId: routineFilter,
      limit,
    }).then(r => r.data),
    refetchInterval: 20_000,
  });

  const executions: Execution[] = data?.executions ?? [];
  const routineMap = new Map(routines.map(r => [r.id, r.name]));

  function toggle(id: number) {
    const next = new Set(expanded);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpanded(next);
  }

  return (
    <div className="container-app py-8 space-y-6">
      <div>
        <Link to="/settings/agent" className="text-xs text-muted-foreground hover:underline flex items-center gap-1 mb-2">
          <ArrowLeft className="w-3 h-3" /> Voltar para agentes
        </Link>
        <h1 className="text-2xl font-semibold">Logs de execução</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Histórico das últimas execuções de rotinas pelos agentes ETL.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg border border-zinc-200 bg-zinc-50">
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium">Status</label>
          <select
            className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">todos</option>
            <option value="ok">ok</option>
            <option value="error">error</option>
            <option value="partial">partial</option>
            <option value="empty">empty</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium">Rotina</label>
          <select
            className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm"
            value={routineFilter ?? ''}
            onChange={(e) => setRoutineFilter(e.target.value ? Number(e.target.value) : undefined)}
          >
            <option value="">todas</option>
            {routines.map(r => <option key={r.id} value={r.id}>{r.name} (#{r.id})</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium">Limite</label>
          <select
            className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
          >
            {[25, 50, 100, 250, 500].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`w-3 h-3 mr-1 ${isFetching ? 'animate-spin' : ''}`} /> Atualizar
        </Button>
        <div className="ml-auto text-xs text-muted-foreground">
          {executions.length} resultado{executions.length === 1 ? '' : 's'}
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Carregando…</div>
      ) : !executions.length ? (
        <div className="text-sm text-muted-foreground italic p-8 text-center border-2 border-dashed border-zinc-200 rounded-lg">
          Nenhuma execução no filtro atual.
        </div>
      ) : (
        <div className="space-y-2">
          {executions.map(e => {
            const style = STATUS_STYLES[e.status] ?? STATUS_STYLES.empty;
            return (
              <div key={e.id} className={`rounded-md border p-3 ${style.tone}`}>
                <div className="flex items-center gap-2 text-sm">
                  {style.icon}
                  <span className="font-medium">{routineMap.get(e.routine_id) || `rotina #${e.routine_id}`}</span>
                  <Badge variant="outline">{e.status}</Badge>
                  <span className="text-xs text-muted-foreground">
                    {timeAgo(e.started_at)} · {e.duration_ms != null ? `${e.duration_ms}ms` : '—'}
                  </span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {e.rows_processed != null ? `${e.rows_processed} row${e.rows_processed === 1 ? '' : 's'}` : ''}
                  </span>
                  {e.error_message && (
                    <button
                      className="text-xs text-zinc-500 hover:text-zinc-700 ml-2"
                      onClick={() => toggle(e.id)}
                    >
                      {expanded.has(e.id) ? 'menos ▲' : 'detalhes ▼'}
                    </button>
                  )}
                </div>
                {expanded.has(e.id) && e.error_message && (
                  <pre className="mt-2 text-xs font-mono bg-white border border-red-200 rounded p-2 overflow-x-auto whitespace-pre-wrap">
                    {e.error_message}
                  </pre>
                )}
                <div className="text-[10px] text-zinc-400 mt-1 font-mono">
                  exec #{e.id} · agent {e.agent_id.slice(0, 8)}… · {new Date(e.started_at).toLocaleString('pt-BR')}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
